import React from 'react';
import { useNavigate, useSubmit, useActionData, useLoaderData, redirect } from 'react-router-dom';
import { AuthProvider } from '../../auth_provider';
import { ContentFrame } from '../../components/content_frame/content_frame';
import { getModels, getModel, setUrlParameters } from '../../utils/functions';
import { DESKTOP_PATH, COMPLAINT_CASE_LIST_PATH, COMPLAINT_CASE_ADD_PATH, COMPLAINT_CASE_EDIT_PATH, LOGIN_PATH } from '../../constants/paths';
import { PATIENTS_GET_API, COMPLAINT_CASE_GET_API } from '../../constants/apis';
import { ComplaintCaseData } from './complaint_case_data';


const DEFAULT_COMPLAINT_CASE = {
  patient_id: "",
  notes: ""
};

export async function complaintCaseAddLoader() {
  try {
    let patients = await getModels(PATIENTS_GET_API);
    let complaint_case = DEFAULT_COMPLAINT_CASE;

    return {patients, complaint_case};
  }
  catch(errors) {
    if(errors instanceof Array) {
      for(let error of errors) {
        if(error.code === 209) {
          // Expired user session
          return redirect(LOGIN_PATH);
        }
        else if(error.code === 203) {
          // Not authorized
          return redirect(DESKTOP_PATH);
        }
      }
    }

    throw errors;    
  }
};

export async function complaintCaseEditLoader({ params }) {
  try {
    let patients;
    let complaint_case;

    if (AuthProvider.is_authenticated) {
      patients = getModels(PATIENTS_GET_API);
      complaint_case = getModel(
        setUrlParameters(`${COMPLAINT_CASE_GET_API}${params.complaint_case_id}`, {
          load_result: true
        }));
      
      patients = await patients;
      complaint_case = await complaint_case;
    }
    else {
      patients = await getModels(PATIENTS_GET_API);
      complaint_case = await getModel(
        setUrlParameters(`${COMPLAINT_CASE_GET_API}${params.complaint_case_id}`, {
          load_result: true
        }));
    }

    if (complaint_case.result !== null) {
      complaint_case.result.sort((a, b) => a.complaint_type.name.localeCompare(b.complaint_type.name));
    }

    return {patients, complaint_case};
  }
  catch(errors) {
    if(errors instanceof Array) {
      for(let error of errors) {
        if(error.code === 209) {
          // Expired user session
          return redirect(LOGIN_PATH);
        }
        else if(error.code === 203) {
          // Not authorized
          return redirect(DESKTOP_PATH);
        }
      }
    }

    throw errors;    
  }
};


class ComplaintCase extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      complaint_case: {
        ...this.props.complaint_case
      }
    };
  }

  componentDidUpdate(prevProps) {
    if (prevProps.complaint_case !== this.props.complaint_case) {      
      this.setState({
        complaint_case: {
          ...this.props.complaint_case
        }
      });
    }
  }

  handleInputChange(event) {
    const target = event.target;
    let value = target.type === 'checkbox' ? target.checked : target.value;
    const name = target.name;

    const newData = {...this.state.complaint_case, [name]: value};

    this.setState({
      complaint_case: newData
    });
  }

  inputsAreValid() {    
    return this.state.complaint_case.patient_id && this.state.complaint_case.patient_id > 0;
  }

  clearWarningMessage() {
    this.props.submit(
      {resetWarnings: true}, 
      {
        method: 'POST',
        encType: "application/json"
      });
  }

  async saveData() {
    let data = {...this.state.complaint_case};

    data.patient_id = parseInt(data.patient_id);
    
    this.props.submit(
      {data}, 
      {
        method: this.props.isEdit ? 'PATCH' : 'POST',
        encType: "application/json"
      });
  }

  async onCopyFormLink() {
    this.props.submit(
      {copyFormLink: true}, 
      {
        method: 'POST',
        encType: "application/json"
      });
  }

  async onArchiveComplaintCase() {
    this.props.submit(
      {
        archiveComplaintCase: true,
        has_finished: !this.props.hasFinished
      }, 
      {
        method: 'POST',
        encType: "application/json"
      });
  }

  render() {
    return (
      <ContentFrame
        headerHistory={[
          {
            path: DESKTOP_PATH,
            text: "Área de trabalho"
          },
          {
            path: COMPLAINT_CASE_LIST_PATH,
            text: "Listar casos"
          },
          {
            path: this.props.isEdit ? COMPLAINT_CASE_EDIT_PATH : COMPLAINT_CASE_ADD_PATH,
            text: this.props.isEdit ? 'Editar caso' : 'Adicionar caso'
          },
        ]}
        titleIcon={this.props.isEdit ? <i className="fas fa-edit"></i> : <i className="fas fa-plus"></i>}
        title={this.props.isEdit ? 'Editar caso' : 'Adicionar caso'}
      >

        <ComplaintCaseData
          warningMessage={this.props.warningMessage}
          showWarningMessage={this.props.warningMessage.length > 0}
          patients={this.props.patients}
          complaint_case={this.state.complaint_case}
          onSave={() => this.saveData()}
          onCloseWarning={() => this.clearWarningMessage()}
          enableSave={this.inputsAreValid()}
          handleInputChange={(event) => this.handleInputChange(event)}
          highlights={this.props.parametersToHighlight}
          onCancelPath={COMPLAINT_CASE_LIST_PATH}
          onCopyFormLink={() => this.onCopyFormLink()}
          onArchiveComplaintCase={() => this.onArchiveComplaintCase()}
          formLinkCopied={this.props.formLinkCopied}
          hasFinished={this.props.hasFinished}
          isEdit={this.props.isEdit}
        />

      </ContentFrame>
    );
  }
};

const WrappedComplaintCase = props => {
  const {patients, complaint_case} = useLoaderData();
  const navigate = useNavigate();
  const submit = useSubmit();
  const actionData = useActionData();

  let warningMessage = '';
  let parametersToHighlight = [];
  let formLinkCopied = false;
  let hasFinished = complaint_case.has_finished;

  if (actionData) {
    warningMessage = actionData.message || '';
    parametersToHighlight = actionData.parameters || [];
    formLinkCopied = actionData.formLinkCopied || false;

    if (typeof actionData.complaintCaseHasFinished === "boolean") {
      hasFinished = actionData.complaintCaseHasFinished;
    }  
  }  

  return (
    <ComplaintCase
      navigate={navigate}
      submit={submit}
      warningMessage={warningMessage}
      parametersToHighlight={parametersToHighlight}
      patients={patients}
      complaint_case={complaint_case}
      formLinkCopied={formLinkCopied}
      hasFinished={hasFinished}
      {...props}
    />
  );
};

export { WrappedComplaintCase as ComplaintCase};
